import { Receipt, Search, ShoppingCart, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ORIGINES_VENTE,
  ORIGINE_VENTE_LABELS,
  type OrigineVente,
} from '@sirajoptic/shared';
import { BadgeReste } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input, Select } from '@/components/ui/field';
import { ChargementBloc, EtatErreur, EtatVide } from '@/components/ui/feedback';
import { Pagination } from '@/components/ui/pagination';
import { TBody, TD, TH, THead, TR, Table } from '@/components/ui/table';
import { PageBody, PageHeader } from '@/layouts/AppShell';
import { date, dh } from '@/lib/format';
import { useRegistre } from './api/ventes.api';
import { VenteDialog } from './components/VenteDialog';
import { VenteDrawer } from './components/VenteDrawer';

/**
 * Le registre des ventes — comptoir et commandes réunis.
 *
 * Confirmer une commande, c'est vendre : cet écran le reflète. Mais rien n'est
 * recopié dans la table des ventes, sans quoi le même argent existerait à deux
 * endroits et le chiffre d'affaires doublerait. Les deux origines sont réunies
 * côté serveur, à la lecture.
 */
export function VentesPage() {
  // `?id=` vient de la palette de recherche : le tiroir s'ouvre directement
  // sur la vente, sans obliger à la retrouver dans la liste.
  const [params] = useSearchParams();

  const [recherche, setRecherche] = useState('');
  const [origine, setOrigine] = useState<OrigineVente | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [dialogue, setDialogue] = useState(false);
  const [detail, setDetail] = useState<string | null>(params.get('id'));

  const naviguer = useNavigate();

  const requete = useMemo(
    () => ({
      page,
      limit: 25,
      q: recherche || undefined,
      origine: origine || undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [page, recherche, origine, from, to],
  );

  const { data, isPending, error, refetch, isFetching } = useRegistre(requete);
  const filtresActifs = Boolean(recherche || origine || from || to);

  function reinitialiser() {
    setRecherche('');
    setOrigine('');
    setFrom('');
    setTo('');
    setPage(1);
  }

  return (
    <>
      <PageHeader
        titre="Ventes"
        description="Tout ce qui a été vendu : passages au comptoir et commandes."
        actions={
          <Button
            variant="primary"
            icone={<ShoppingCart aria-hidden className="h-4 w-4" />}
            onClick={() => setDialogue(true)}
          >
            Nouvelle vente
          </Button>
        }
      />

      <PageBody>
        <Card>
          <div className="flex flex-wrap items-center gap-2 px-4 py-3">
            <div className="relative min-w-0 flex-1 sm:max-w-sm">
              <Search
                aria-hidden
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
              />
              <Input
                value={recherche}
                onChange={(e) => {
                  setRecherche(e.target.value);
                  setPage(1);
                }}
                placeholder="Client ou référence…"
                className="pl-8"
                aria-label="Rechercher une vente"
              />
            </div>

            <Select
              value={origine}
              onChange={(e) => {
                setOrigine(e.target.value as OrigineVente | '');
                setPage(1);
              }}
              className="w-auto"
              aria-label="Filtrer par origine"
            >
              <option value="">Toutes origines</option>
              {ORIGINES_VENTE.map((o) => (
                <option key={o} value={o}>
                  {ORIGINE_VENTE_LABELS[o]}
                </option>
              ))}
            </Select>

            <label className="flex items-center gap-2 text-sm text-ink-muted">
              Du
              <Input
                type="date"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setPage(1);
                }}
                className="w-auto"
              />
              au
              <Input
                type="date"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setPage(1);
                }}
                className="w-auto"
              />
            </label>

            {filtresActifs ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={reinitialiser}
                icone={<X aria-hidden className="h-3.5 w-3.5" />}
              >
                Réinitialiser
              </Button>
            ) : null}

            {isFetching ? <span className="text-xs text-ink-muted">Actualisation…</span> : null}
          </div>

          {isPending ? (
            <ChargementBloc />
          ) : error ? (
            <EtatErreur erreur={error} onReessayer={() => void refetch()} />
          ) : data.data.length === 0 ? (
            <EtatVide
              titre="Aucune vente"
              description={
                filtresActifs
                  ? 'Aucune vente ne correspond à ces filtres.'
                  : 'Les passages au comptoir et les commandes apparaîtront ici.'
              }
              icone={<Receipt aria-hidden className="h-6 w-6" />}
              action={
                <Button variant="primary" onClick={() => setDialogue(true)}>
                  Nouvelle vente
                </Button>
              }
            />
          ) : (
            <>
              <Table>
                <THead>
                  <TR>
                    <TH>Date</TH>
                    <TH>Origine</TH>
                    <TH>Référence</TH>
                    <TH>Client</TH>
                    <TH numerique>Total</TH>
                    <TH numerique>Encaissé</TH>
                    <TH numerique>Reste</TH>
                  </TR>
                </THead>
                <TBody>
                  {data.data.map((l) => (
                      <TR
                        key={`${l.origine}-${l.id}`}
                        /* Chaque origine mène à son propre détail : le tiroir
                           de commande existant, ou celui de la vente. */
                        onClick={
                          l.origine === 'COMMANDE'
                            ? () => naviguer(`/commandes?id=${l.id}`)
                            : () => setDetail(l.id)
                        }
                      >
                        <TD className="whitespace-nowrap text-ink-muted">{date(l.date)}</TD>
                        <TD>
                          <span
                            className={
                              l.origine === 'COMPTOIR'
                                ? 'rounded bg-mustard-100 px-1.5 py-0.5 text-xs font-medium text-mustard-800'
                                : 'rounded bg-surface-sunken px-1.5 py-0.5 text-xs font-medium text-ink-soft'
                            }
                          >
                            {ORIGINE_VENTE_LABELS[l.origine]}
                          </span>
                        </TD>
                        <TD className="whitespace-nowrap font-mono text-xs text-ink-muted">
                          {l.reference ?? '—'}
                        </TD>
                        <TD className="font-medium">{l.libelle}</TD>
                        <TD numerique className="font-semibold">
                          {dh(l.total)}
                        </TD>
                        <TD numerique className="text-ink-muted">
                          {dh(l.encaisse)}
                        </TD>
                        <TD>
                          {/* Le même composant que l'écran Commandes : il sait
                              aussi dire « soldée » et signaler un trop-perçu,
                              qu'un simple « — » masquerait. */}
                          <BadgeReste reste={l.total - l.encaisse} />
                        </TD>
                      </TR>
                    ))}
                </TBody>
              </Table>
              <Pagination meta={data.meta} onPage={setPage} libelle="ventes" />
            </>
          )}
        </Card>
      </PageBody>

      <VenteDialog ouvert={dialogue} onFermer={() => setDialogue(false)} />

      <VenteDrawer id={detail} onFermer={() => setDetail(null)} />
    </>
  );
}
